import React, { useState, useEffect } from 'react';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { AddCircleOutline, CompareArrows, RemoveCircleOutline, Save as SaveIcon, Warning as WarningIcon } from '@mui/icons-material';
import dayjs from 'dayjs';
import Layout from '../components/Layout';
import { Kpi, Panel } from '../components/ui';
import { tokens } from '../theme';

const PhysicalInventory = () => {
  const [inventoryDate, setInventoryDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [stockData, setStockData] = useState([]);
  const [physicalCounts, setPhysicalCounts] = useState({});
  const [differences, setDifferences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    loadStockData();
  }, [inventoryDate]);

  const loadStockData = async () => {
    setLoading(true);
    try {
      // Récupérer le dashboard quotidien qui contient les stocks
      const stockResponse = await fetch(`/api/stock/dashboard-quotidien?date=${inventoryDate}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const stockData = stockResponse.ok ? await stockResponse.json() : [];
      
      // Récupérer tous les types PBA
      const pbaTypesResponse = await fetch('/api/pba-types', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const allPbaTypes = await pbaTypesResponse.json();
      
      // Créer un array avec TOUS les types PBA et leurs stocks
      const stockArray = allPbaTypes.map(pbaType => {
        const stockInfo = stockData.find(s => s.code === pbaType.code);
        const stock = stockInfo ? parseInt(stockInfo.stock_total) || 0 : 0;
        return {
          pba_type: pbaType.code,
          stock_theorique: stock,
          stock_physique: 0
        };
      });

      setStockData(stockArray);
      
      // Initialiser les comptages physiques pour TOUS les types
      const initialCounts = {};
      allPbaTypes.forEach(pbaType => {
        initialCounts[pbaType.code] = '';
      });
      setPhysicalCounts(initialCounts);

    } catch (error) {
      console.error('Erreur chargement stock:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhysicalCountChange = (pbaType, value) => {
    const numValue = parseInt(value) || 0;
    setPhysicalCounts(prev => ({
      ...prev,
      [pbaType]: value
    }));

    // Calculer les différences en temps réel
    const theorique = stockData.find(item => item.pba_type === pbaType)?.stock_theorique || 0;
    const difference = numValue - theorique;
    
    setDifferences(prev => {
      const newDiffs = prev.filter(d => d.pba_type !== pbaType);
      if (difference !== 0 && value !== '') {
        newDiffs.push({
          pba_type: pbaType,
          stock_theorique: theorique,
          stock_physique: numValue,
          difference: difference,
          type_ecart: difference > 0 ? 'EXCEDENT' : 'MANQUANT'
        });
      }
      return newDiffs;
    });
  };

  const saveInventory = async () => {
    try {
      setLoading(true);
      
      // Récupérer les IDs des types PBA
      const pbaTypesResponse = await fetch('/api/pba-types', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const pbaTypes = await pbaTypesResponse.json();

      // Toutes les lignes effectivement comptées, y compris un comptage à 0
      const inventories = stockData
        .filter(item => physicalCounts[item.pba_type] !== '' && physicalCounts[item.pba_type] !== undefined)
        .map(item => ({
          pba_type_id: pbaTypes.find(p => p.code === item.pba_type)?.id,
          physical_quantity: parseInt(physicalCounts[item.pba_type], 10) || 0,
          notes: `Inventaire physique ${item.pba_type}`
        }));

      if (inventories.length === 0) {
        alert('Saisissez au moins un comptage avant d\'enregistrer.');
        return;
      }

      // Le serveur enregistre l'inventaire ET aligne le stock sur le comptage (une seule opération)
      const response = await fetch('/api/stock/inventaires', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          inventory_date: inventoryDate,
          inventories: inventories
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Erreur ${response.status}`);
      }

      alert('Inventaire physique enregistré avec succès ! Le stock a été aligné sur le comptage.');
      setShowConfirmDialog(false);
      setDifferences([]);
      loadStockData(); // Recharger
    } catch (error) {
      console.error('Erreur sauvegarde inventaire:', error);
      alert(`Erreur lors de l'enregistrement de l'inventaire : ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const totalDifferences = differences.reduce((sum, diff) => sum + Math.abs(diff.difference), 0);
  const hasExcedent = differences.some(d => d.difference > 0);
  const hasManquant = differences.some(d => d.difference < 0);

  const exc = differences.filter((x) => x.difference > 0).reduce((t, x) => t + x.difference, 0);
  const man = differences.filter((x) => x.difference < 0).reduce((t, x) => t + x.difference, 0);

  return (
    <Layout title="Inventaire physique" plain>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2.5 }}>
        <TextField type="date" size="small" label="Date d'inventaire" value={inventoryDate}
          onChange={(e) => setInventoryDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button variant="outlined" onClick={loadStockData} disabled={loading}>Actualiser le stock théorique</Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}><Kpi icon={CompareArrows} tint="navy" label="Types avec écart" value={differences.length} sub={`Écart cumulé ${totalDifferences}`} /></Grid>
        <Grid item xs={6} sm={4}><Kpi icon={AddCircleOutline} tint="green" label="Excédents" value={exc ? `+${exc}` : 0} sub="Seront ajoutés au stock" /></Grid>
        <Grid item xs={6} sm={4}><Kpi icon={RemoveCircleOutline} tint="red" label="Manquants" value={man ? `−${Math.abs(man)}` : 0} sub="Seront retirés du stock" valueColor={man ? '#B4232A' : undefined} /></Grid>
      </Grid>

      <Panel title="Comptage physique" flush sx={{ mb: 2 }}
        action={<Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Saisissez la quantité comptée pour chaque type</Typography>}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type PBA</TableCell><TableCell align="right">Théorique</TableCell><TableCell align="right">Compté</TableCell>
                <TableCell align="right">Écart</TableCell><TableCell>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stockData.map((item) => {
                const physicalCount = parseInt(physicalCounts[item.pba_type], 10) || 0;
                const difference = physicalCount - item.stock_theorique;
                const hasValue = physicalCounts[item.pba_type] !== '' && physicalCounts[item.pba_type] !== undefined;
                return (
                  <TableRow key={item.pba_type} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{item.pba_type}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600 }}>{item.stock_theorique}</TableCell>
                    <TableCell align="right">
                      <TextField type="number" size="small" value={physicalCounts[item.pba_type] ?? ''} placeholder="—"
                        onChange={(e) => handlePhysicalCountChange(item.pba_type, e.target.value)}
                        inputProps={{ min: 0, 'aria-label': `Quantité comptée ${item.pba_type}`, style: { textAlign: 'right', fontFamily: tokens.mono } }} sx={{ width: 110 }} />
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, fontWeight: 600, color: !hasValue || difference === 0 ? 'text.secondary' : difference > 0 ? 'success.main' : 'error.main' }}>
                      {hasValue ? `${difference > 0 ? '+' : difference < 0 ? '−' : ''}${Math.abs(difference)}` : '—'}
                    </TableCell>
                    <TableCell>
                      {hasValue && <Chip size="small" variant="outlined"
                        label={difference === 0 ? 'Conforme' : difference > 0 ? 'Excédent' : 'Manquant'}
                        color={difference === 0 ? 'default' : difference > 0 ? 'success' : 'error'} />}
                    </TableCell>
                  </TableRow>
                );
              })}
              {stockData.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>{loading ? 'Chargement…' : 'Aucun stock théorique'}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Panel>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={() => setShowConfirmDialog(true)}
          disabled={loading || Object.values(physicalCounts).every((v) => v === '')}>
          {loading ? 'Traitement…' : "Enregistrer l'inventaire"}
        </Button>
      </Box>

        {/* Dialog de confirmation */}
        <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="warning" />
              Confirmer l'Inventaire Physique
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Cette action va ajuster automatiquement les stocks selon les comptages physiques.
            </Alert>
            
            {differences.length > 0 && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Écarts détectés :
                </Typography>
                <TableContainer sx={{ mb: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type PBA</TableCell>
                        <TableCell align="right">Théorique</TableCell>
                        <TableCell align="right">Physique</TableCell>
                        <TableCell align="right">Écart</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {differences.map((diff) => (
                        <TableRow key={diff.pba_type}>
                          <TableCell>{diff.pba_type}</TableCell>
                          <TableCell align="right">{diff.stock_theorique}</TableCell>
                          <TableCell align="right">{diff.stock_physique}</TableCell>
                          <TableCell align="right">
                            <Typography color={diff.difference > 0 ? 'info.main' : 'error.main'}>
                              {diff.difference > 0 ? '+' : ''}{diff.difference}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={diff.difference > 0 ? 'Ajout au stock' : 'Retrait du stock'}
                              color={diff.difference > 0 ? 'info' : 'error'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowConfirmDialog(false)}>Annuler</Button>
            <Button 
              variant="contained" 
              onClick={saveInventory} 
              disabled={loading}
            >
              {loading ? 'Enregistrement...' : 'Confirmer et Ajuster'}
            </Button>
          </DialogActions>
        </Dialog>
    </Layout>
  );
};

export default PhysicalInventory;